/**
 * ⌘K / Ctrl+K で開く検索。UX1-a では**ナビ項目名の絞り込みジャンプだけ**
 * (データの横断検索は後の段階)。
 *
 * 一覧は antd の Menu にしない — アプリ内ナビの `role="menu"` と二重になり、
 * `getByRole("menu")` で項目を探す e2e が曖昧になる。
 */
import { SearchOutlined } from "@ant-design/icons";
import { Input, Modal, type InputRef } from "antd";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

export type PaletteEntry = { label: string; group: string; to: string };

export function CommandPalette({
  open,
  entries,
  onClose,
}: {
  open: boolean;
  entries: PaletteEntry[];
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const listId = useId();
  const optionId = (i: number) => `${listId}-opt-${i}`;
  // 開くたびに入力欄へ。閉じる途中で開き直すと Input が作り直されず autoFocus が効かない
  const inputRef = useRef<InputRef>(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setActive(0);
    }
  }, [open]);

  const hits = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const rows = needle
      ? entries.filter(
          (e) =>
            e.label.toLowerCase().includes(needle) || e.group.toLowerCase().includes(needle),
        )
      : entries;
    return rows.slice(0, 12);
  }, [entries, q]);

  const go = (e: PaletteEntry | undefined) => {
    if (!e) return;
    onClose();
    navigate(e.to);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={560}
      destroyOnHidden
      afterOpenChange={(o) => {
        if (!o) return;
        // 既に本体(入力欄・候補ボタン)の中にフォーカスがあるなら奪わない(Tab で候補へ
        // 移った直後にアニメーション終了で戻されない)。body や rc-dialog の番兵なら入力欄へ
        const el = inputRef.current?.input;
        if (el && !el.closest(".ant-modal-body")?.contains(document.activeElement)) {
          inputRef.current?.focus();
        }
      }}
      title={null}
      styles={{ body: { padding: 8 } }}
    >
      <Input
        ref={inputRef}
        autoFocus
        size="large"
        prefix={<SearchOutlined />}
        placeholder="画面の名前で絞り込む(例: 従業員)"
        value={q}
        onChange={(ev) => {
          setQ(ev.target.value);
          setActive(0);
        }}
        role="combobox"
        aria-expanded={hits.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={hits.length > 0 ? optionId(active) : undefined}
        onKeyDown={(ev) => {
          // IME 変換中の Enter / 矢印は候補の確定・移動(変換の操作)。画面の移動に使わない(UX1-a 中4)
          if (ev.nativeEvent.isComposing || ev.keyCode === 229) {
            // 変換を取り消す Escape は Modal(rc-dialog は keyCode 27 で閉じる)にも渡さない
            if (ev.key === "Escape") ev.stopPropagation();
            return;
          }
          if (ev.key === "ArrowDown") {
            ev.preventDefault();
            setActive((i) => Math.min(i + 1, hits.length - 1));
          } else if (ev.key === "ArrowUp") {
            ev.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
          } else if (ev.key === "Enter") {
            ev.preventDefault();
            go(hits[active]);
          } else if (ev.key === "Escape") {
            onClose();
          }
        }}
      />
      <div className="ux-cmdk-list" role="listbox" aria-label="画面の候補" id={listId}>
        {hits.length === 0 && <div className="ux-cmdk-empty">一致する画面が無い</div>}
        {hits.map((e, i) => (
          <button
            key={`${e.to}:${e.label}`}
            id={optionId(i)}
            type="button"
            role="option"
            aria-selected={i === active}
            className={`ux-cmdk-item${i === active ? " is-active" : ""}`}
            onMouseEnter={() => setActive(i)}
            onClick={() => go(e)}
          >
            <span>{e.label}</span>
            <span className="ux-cmdk-group">{e.group}</span>
          </button>
        ))}
      </div>
    </Modal>
  );
}
